// src/pages/SignupPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  User,
  Lock,
  Mail,
  Phone,
  ArrowRight,
  CheckCircle,
  X,
  AlertCircle,
  Home,
  RefreshCw,
  Shield,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Logo from "../../assets/logo.png";
import axios from "axios";
import { BASE_URL } from "../../hooks/useAuth";

export default function SignupPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1: 정보입력, 2: 이메일인증
  const [formData, setFormData] = useState({
    x_id: "",
    password: "",
    passwordConfirm: "",
    email: "",
    hp: "",
  });
  const [verificationCode, setVerificationCode] = useState("");
  const [inputCode, setInputCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [idChecked, setIdChecked] = useState(false); // 아이디 중복체크 여부
  const [idAvailable, setIdAvailable] = useState(false); // 아이디 사용가능 여부

  // 타이머 관련
  const [timeLeft, setTimeLeft] = useState(600); // 10분 = 600초
  const [isTimerActive, setIsTimerActive] = useState(false);

  // 타이머 효과
  useEffect(() => {
    let interval = null;
    if (isTimerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((time) => time - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsTimerActive(false);
      setError("인증 시간이 만료되었습니다. 다시 전송해주세요.");
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerActive, timeLeft]);

  // 시간 포맷팅
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // 아이디 중복체크
  const checkDuplicateId = async () => {
    if (!formData.x_id) {
      setError("아이디를 입력해주세요.");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${BASE_URL}/duplicate_x_id_20250821`, {
        x_id: formData.x_id,
      });

      if (data.status && data.duplicate) {
        setError("이미 사용중인 아이디입니다.");
        setIdAvailable(false);
        setIdChecked(true);
      } else if (data.status) {
        setToastMessage("사용 가능한 아이디입니다.");
        setShowToast(true);
        setIdAvailable(true);
        setIdChecked(true);
      }
    } catch (err) {
      setError("아이디 중복체크 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 아이디 변경시 중복체크 초기화
  const handleIdChange = (value) => {
    setFormData({ ...formData, x_id: value });
    setIdChecked(false);
    setIdAvailable(false);
    setError(""); // 에러 메시지도 초기화
  };

  // 이메일 인증 전송
  const sendVerification = async () => {
    setIsLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${BASE_URL}/email_verification_20250821`,
        {
          email: formData.email,
        }
      );

      if (data.status && data.already) {
        setError("이미 사용중인 이메일입니다.");
        return;
      }

      if (data.status) {
        setVerificationCode(data.auth_code);
        setStep(2);
        setTimeLeft(600);
        setIsTimerActive(true);
        // 첫 전송시에만 토스트 메시지 표시
        if (!verificationCode) {
          setToastMessage("인증코드가 발송되었습니다.");
          setShowToast(true);
        }
      } else {
        setError("이메일 전송에 실패했습니다.");
      }
    } catch (err) {
      setError("이메일 전송 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 인증코드 재전송
  const resendCode = async () => {
    setInputCode("");
    setToastMessage("인증코드가 재전송되었습니다.");
    setShowToast(true);
    await sendVerification();
  };

  // 회원가입 완료
  const completeSignup = async () => {
    if (inputCode !== verificationCode) {
      setError("인증코드가 일치하지 않습니다.");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${BASE_URL}/signup_20250821`, {
        x_id: formData.x_id,
        password: formData.password,
        email: formData.email,
        hp: formData.hp,
      });

      if (data.status) {
        setToastMessage("회원가입이 완료되었습니다!");
        setShowToast(true);
        setTimeout(() => nav("/login"), 1500);
      } else {
        setError(data.error || "회원가입에 실패했습니다.");
      }
    } catch (err) {
      setError("회원가입 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1 제출
  const handleStep1Submit = (e) => {
    e.preventDefault();

    // 아이디 중복체크 확인
    if (!idChecked) {
      setError("아이디 중복체크를 해주세요.");
      return;
    }

    if (!idAvailable) {
      setError("사용할 수 없는 아이디입니다.");
      return;
    }

    if (formData.password !== formData.passwordConfirm) {
      setError("비밀번호가 일치하지 않습니다.");
      return;
    }

    if (formData.password.length < 8) {
      setError("비밀번호는 8자 이상이어야 합니다.");
      return;
    }

    sendVerification();
  };

  // --- Toast 콜백을 안정화(useCallback)해서 리렌더 시 타이머 리셋 방지 ---
  const handleToastDone = useCallback(() => {
    setShowToast(false);
  }, []);

  // Toast 컴포넌트
  const Toast = ({ open, text, onDone }) => {
    useEffect(() => {
      if (!open) return;
      const timer = setTimeout(() => onDone(), 3000);
      return () => clearTimeout(timer);
      // 의존성을 open만 두면, 부모 리렌더(예: 타이머 감소) 시에도 타이머가 리셋되지 않음
    }, [open]); // <-- onDone을 의존성에서 제외
    if (!open) return null;
    return (
      <div className="fixed top-6 right-6 z-50 animate-slide-in">
        <div className="bg-gray-900/95 backdrop-blur-xl border border-gray-800 rounded-2xl px-6 py-4 flex items-center gap-3 shadow-2xl">
          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-white" />
          </div>
          <p className="text-white font-medium">{text}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Animated Background - 로그인과 동일 */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600 rounded-full filter blur-[150px] opacity-30 animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600 rounded-full filter blur-[150px] opacity-30 animate-pulse delay-700"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-600 rounded-full filter blur-[200px] opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 1px)`,
              backgroundSize: "50px 50px",
            }}
          ></div>
        </div>
      </div>

      <div className="relative z-10 min-h-screen flex">
        <button
          onClick={() => nav("/login")}
          className="absolute top-6 left-6 flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
        >
          <Home className="w-5 h-5" />
          <span className="font-medium">로그인으로</span>
        </button>

        <div className="w-full max-w-5xl mx-auto flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md">
            <div className="bg-gray-900/50 backdrop-blur-xl border border-gray-800 rounded-3xl p-8">
              {/* 로고 */}
              <div className="flex justify-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl">
                  <img src={Logo} alt="elva logo" className="w-20 h-20" />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-white mb-2 text-center">
                {step === 1 ? "회원가입" : "이메일 인증"}
              </h2>
              <p className="text-gray-400 mb-8 text-center">
                {step === 1
                  ? "Elva 서비스에 가입하세요"
                  : "인증코드를 입력해주세요"}
              </p>

              {/* Step Indicator */}
              <div className="flex items-center justify-center mb-8">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      step >= 1 ? "bg-blue-600" : "bg-gray-700"
                    }`}
                  >
                    <span className="text-sm font-semibold">1</span>
                  </div>
                  <div
                    className={`w-20 h-1 ${
                      step >= 2 ? "bg-blue-600" : "bg-gray-700"
                    }`}
                  ></div>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      step >= 2 ? "bg-blue-600" : "bg-gray-700"
                    }`}
                  >
                    <span className="text-sm font-semibold">2</span>
                  </div>
                </div>
              </div>

              {step === 1 ? (
                <form onSubmit={handleStep1Submit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      아이디
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                          type="text"
                          required
                          value={formData.x_id}
                          onChange={(e) => handleIdChange(e.target.value)}
                          className={`w-full pl-12 pr-4 py-3 bg-gray-800/50 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:bg-gray-800 transition-all ${
                            idChecked && idAvailable
                              ? "border-green-500 focus:border-green-500"
                              : idChecked && !idAvailable
                              ? "border-red-500 focus:border-red-500"
                              : "border-gray-700 focus:border-blue-500"
                          }`}
                          placeholder="아이디를 입력하세요"
                        />
                        {idChecked && idAvailable && (
                          <CheckCircle className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                        )}
                        {idChecked && !idAvailable && (
                          <X className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={checkDuplicateId}
                        disabled={isLoading || !formData.x_id}
                        className="px-4 py-3 bg-gray-800/50 hover:bg-gray-800 border border-gray-700 rounded-xl text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        중복체크
                      </button>
                    </div>
                    {idChecked && idAvailable && (
                      <p className="mt-2 text-sm text-green-400">
                        사용 가능한 아이디입니다.
                      </p>
                    )}
                    {idChecked && !idAvailable && (
                      <p className="mt-2 text-sm text-red-400">
                        이미 사용중인 아이디입니다.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      비밀번호
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="password"
                        required
                        value={formData.password}
                        onChange={(e) =>
                          setFormData({ ...formData, password: e.target.value })
                        }
                        className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-gray-800 transition-all"
                        placeholder="비밀번호 (8자 이상)"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      비밀번호 확인
                    </label>
                    <div className="relative">
                      <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="password"
                        required
                        value={formData.passwordConfirm}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            passwordConfirm: e.target.value,
                          })
                        }
                        className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-gray-800 transition-all"
                        placeholder="비밀번호를 다시 입력하세요"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      이메일
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-gray-800 transition-all"
                        placeholder="이메일 주소"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      휴대폰 번호
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="tel"
                        required
                        value={formData.hp}
                        onChange={(e) =>
                          setFormData({ ...formData, hp: e.target.value })
                        }
                        className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-gray-800 transition-all"
                        placeholder="010-0000-0000"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-400" />
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full group bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        처리 중...
                      </>
                    ) : (
                      <>
                        다음 단계
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>
                </form>
              ) : (
                <div className="space-y-6">
                  {/* 타이머 */}
                  <div className="bg-gray-800/50 rounded-xl p-4 text-center">
                    <p className="text-gray-400 text-sm mb-2">남은 시간</p>
                    <p
                      className={`text-2xl font-bold ${
                        timeLeft < 60 ? "text-red-400" : "text-blue-400"
                      }`}
                    >
                      {formatTime(timeLeft)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      인증코드
                    </label>
                    <input
                      type="text"
                      value={inputCode}
                      onChange={(e) => setInputCode(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-gray-800 transition-all text-center text-xl tracking-widest"
                      placeholder="6자리 코드"
                      maxLength="6"
                    />
                  </div>

                  {error && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-400" />
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  <button
                    onClick={completeSignup}
                    disabled={isLoading || !inputCode || timeLeft === 0}
                    className="w-full group bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        처리 중...
                      </>
                    ) : (
                      <>
                        회원가입 완료
                        <CheckCircle className="w-5 h-5" />
                      </>
                    )}
                  </button>

                  <div className="flex flex-col gap-3">
                    <button
                      onClick={resendCode}
                      disabled={isLoading || timeLeft > 540} // 9분 이상 남았을 때는 재전송 불가
                      className="w-full bg-gray-800/50 hover:bg-gray-800 text-gray-300 py-2 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      인증코드 재전송
                    </button>

                    <p className="text-center text-gray-500 text-sm">
                      인증코드를 받지 못하셨나요?
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-8 pt-8 border-t border-gray-800 text-center">
                <p className="text-gray-400">
                  이미 계정이 있으신가요?{" "}
                  <button
                    className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
                    onClick={() => nav("/login")}
                  >
                    로그인
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Toast open={showToast} text={toastMessage} onDone={handleToastDone} />

      <style jsx>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
